import axios from 'axios';
import type { NormalizedApiError } from './types';

const AXIOS_TRANSPORT_MESSAGE = /^Request failed with status code \d+$/i;

const CODE_FALLBACK_MESSAGES: Record<string, string> = {
  INVALID_CREDENTIALS: 'Invalid email or password.',
  AUTH_RATE_LIMIT: 'Too many authentication attempts. Please wait and try again.',
  RATE_LIMIT_EXCEEDED: 'Too many requests. Please wait and try again.',
  CRAWL_RATE_LIMIT: 'Crawler actions are rate-limited. Please try again later.',
  ACCOUNT_LOCKED: 'This account is locked. Please contact an administrator.',
  MFA_REQUIRED: 'Please enter your MFA code.',
  MFA_INVALID: 'Invalid MFA code. Please try again.',
  MFA_NOT_SETUP: 'MFA is not set up for this account.',
  TOKEN_INVALID: 'Your session has expired. Please sign in again.',
  UNAUTHORIZED: 'Please sign in to continue.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  BAD_REQUEST: 'Please check your request and try again.',
  CONFLICT: 'This action conflicts with existing data.',
  VALIDATION_ERROR: 'Please check the highlighted fields and try again.',
  INTEGRATION_ERROR: 'The integration service is temporarily unavailable.',
  INTERNAL_ERROR: 'Something went wrong. Please try again.',
};

const STATUS_FALLBACK_MESSAGES: Record<number, string> = {
  400: 'Please check your request and try again.',
  401: 'Please sign in to continue.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested item could not be found.',
  409: 'This action conflicts with existing data.',
  423: 'This account is locked. Please contact an administrator.',
  428: 'Please complete the required authentication step.',
  429: 'Too many requests. Please wait and try again.',
  500: 'Something went wrong on the server. Please try again.',
  502: 'The service is temporarily unavailable. Please try again.',
  503: 'The service is temporarily unavailable. Please try again.',
  504: 'The service timed out. Please try again.',
};

function firstString(...values: unknown[]): string | undefined {
  return values.find((value): value is string => typeof value === 'string' && value.trim().length > 0)?.trim();
}

function extractCode(payload: any): string | undefined {
  return firstString(
    payload?.code,
    payload?.error?.code,
    payload?.message?.code
  );
}

function extractBackendMessage(payload: any): string | undefined {
  return firstString(
    payload?.message,
    payload?.error?.message,
    payload?.message?.message,
    payload?.data?.message
  );
}

function isUsableUserMessage(message?: string): message is string {
  return typeof message === 'string' && message.length > 0 && !AXIOS_TRANSPORT_MESSAGE.test(message);
}

function fallbackMessage(status?: number, code?: string): string {
  if (code && CODE_FALLBACK_MESSAGES[code]) return CODE_FALLBACK_MESSAGES[code];
  if (status && STATUS_FALLBACK_MESSAGES[status]) return STATUS_FALLBACK_MESSAGES[status];
  return 'Something went wrong. Please try again.';
}

export function normalizeApiError(error: unknown): NormalizedApiError {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as any;
    const status = error.response?.status;
    const code = extractCode(payload);
    const backendMessage = extractBackendMessage(payload);

    return {
      status,
      code,
      message: isUsableUserMessage(backendMessage)
        ? backendMessage
        : fallbackMessage(status, code),
      details: payload?.details ?? payload?.error?.details,
    };
  }

  if (error instanceof Error) {
    return {
      message: isUsableUserMessage(error.message)
        ? error.message
        : 'Something went wrong. Please try again.',
    };
  }

  return { message: 'Something went wrong. Please try again.' };
}
